/**
 * What a plant card's week does when the outlook has no row for it (B28).
 *
 * The week's items and marks come from the plants brief, its grid from the
 * outlook: two requests. After seed packets are filed the outlook is read
 * again, and if that read fails the old outlook, which has no row for the
 * new plants, is kept. This decides, without judging the week itself (the
 * server grades it), whether the card draws, waits, reads the outlook again
 * once, says nothing, or says the standard failure sentence (D31).
 *
 * @module outlookRow
 */

/** What the card does about its outlook row. */
export type OutlookRowState =
  /** The row is there: draw the grid. */
  | 'draw'
  /** A read is in flight: show the loading line. */
  | 'wait'
  /** No row for a week the brief has: read the outlook again, once. */
  | 'refetch'
  /** No row and no failure on our side: no grid, no words, no report. */
  | 'quiet'
  /** The outlook request failed on our side: the standard sentence, reported. */
  | 'failure';

/** What the decision reads. */
export interface OutlookRowInput {
  /** Whether the outlook has a row for this plant. */
  hasOutlook: boolean;
  /** Whether the plants brief has a week for this plant. */
  hasWeek: boolean;
  /** Whether either request is in flight. */
  fetching: boolean;
  /** Whether the outlook's failure is on our side (isServerFailure: 5xx or no answer). */
  serverFailure: boolean;
  /** Whether the card has already read the outlook again for this missing row on the outlook now held (not on an earlier one). */
  refetched: boolean;
}

/**
 * Decides what a card does about its outlook row.
 *
 * @param input - OutlookRowInput.
 * @returns The state to act on.
 */
export function outlookRowState(input: OutlookRowInput): OutlookRowState {
  if (input.hasOutlook && input.hasWeek) return 'draw';
  if (input.fetching) return 'wait';
  if (!input.hasWeek) return 'quiet';
  if (!input.refetched) return 'refetch';
  return input.serverFailure ? 'failure' : 'quiet';
}
